import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Component, TestCase, TestStep, Feature, TestCaseStatus, TestCaseComment } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useProduct } from '../../contexts/ProductContext';
import { useToast, showTempMessage } from '../../utils/messages';
import { Toast } from '../../utils/messages';
import { apiRequest } from '../../utils/api';
import '../../App.css';

const EditTestCasePage: React.FC = () => {
  const navigate = useNavigate();
  const { testCaseId } = useParams<{ testCaseId: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedProduct } = useProduct();
  const { message, setMessage } = useToast();
  
  const isEditMode = !!testCaseId && testCaseId !== 'new';
  
  // Get prefill data from location state
  const prefillData = location.state as { componentId?: string; featureId?: string } | null;

  // Data
  const [features, setFeatures] = useState<Feature[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [testCaseTitle, setTestCaseTitle] = useState('');
  const [testCaseDescription, setTestCaseDescription] = useState('');
  const [testCasePreConditions, setTestCasePreConditions] = useState('');
  const [selectedComponentId, setSelectedComponentId] = useState<string>(prefillData?.componentId || '');
  const [selectedFeatureId, setSelectedFeatureId] = useState<string>(prefillData?.featureId || '');
  const [steps, setSteps] = useState<TestStep[]>([]);
  const [showStepsSection, setShowStepsSection] = useState(false);
  const [isAutomated, setIsAutomated] = useState(false);
  const [isAutomationCandidate, setIsAutomationCandidate] = useState(false);
  const [status, setStatus] = useState<TestCaseStatus>('Active');
  const [comments, setComments] = useState<TestCaseComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [testCaseNumber, setTestCaseNumber] = useState<number | undefined>();
  const [testCaseUUID, setTestCaseUUID] = useState<string | null>(null); // Store the actual UUID for API calls

  // Filtered components based on selected feature
  const filteredComponents = useMemo(() => {
    if (!selectedFeatureId) return components;
    return components.filter(c => c.featureId === selectedFeatureId);
  }, [selectedFeatureId, components]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!selectedProduct?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch features and components
        const [featuresData, componentsData] = await Promise.all([
          apiRequest<Feature[]>(`/api/features?productId=${selectedProduct.id}`, {}, { useAuth: true }),
          apiRequest<Component[]>('/api/components', {}, { useAuth: true })
        ]);

        setFeatures(featuresData || []);
        
        // Filter components by product features
        const featureIds = new Set((featuresData || []).map(f => f.id));
        const filtered = (componentsData || []).filter(c => featureIds.has(c.featureId));
        setComponents(filtered);

        // If editing, load the test case
        if (isEditMode && testCaseId) {
          console.log('Loading test case with ID:', testCaseId);
          const testCase = await apiRequest<TestCase>(`/api/testcases/${testCaseId}`, {}, { useAuth: true });
          console.log('Loaded test case:', testCase);
          if (testCase) {
            setTestCaseTitle(testCase.title || '');
            setTestCaseDescription(testCase.description || '');
            setTestCasePreConditions(testCase.preConditions || '');
            setSelectedComponentId(testCase.componentId || '');
            // Get featureId from the component if not directly set
            const derivedFeatureId = testCase.featureId || 
              (testCase.componentId ? filtered.find(c => c.id === testCase.componentId)?.featureId : '') || '';
            setSelectedFeatureId(derivedFeatureId);
            setSteps(testCase.steps || []);
            setShowStepsSection((testCase.steps || []).length > 0);
            setIsAutomated(testCase.automated || false);
            setIsAutomationCandidate(testCase.automationCandidate || false);
            setStatus(testCase.status || 'Active');
            setComments(testCase.comments || []);
            setTestCaseNumber(testCase.testCaseNumber);
            setTestCaseUUID(testCase.id); // Store the actual UUID for API updates
          } else {
            showTempMessage(setMessage, { type: 'error', text: 'Test case not found' });
            navigate('/library');
          }
        } else if (prefillData?.componentId) {
          // Set feature from prefilled component
          const comp = filtered.find(c => c.id === prefillData.componentId);
          if (comp) {
            setSelectedFeatureId(comp.featureId);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        showTempMessage(setMessage, { type: 'error', text: 'Failed to load data' });
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProduct?.id, testCaseId, isEditMode, prefillData, setMessage, navigate]);

  const handleAddStep = () => {
    const newStep: TestStep = {
      id: `step-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      action: '',
      data: '',
      expectedResult: '',
      order: steps.length + 1
    };
    setSteps([...steps, newStep]);
  };

  const handleUpdateStep = (stepId: string, field: keyof TestStep, value: string) => {
    setSteps(steps.map(step => 
      step.id === stepId ? { ...step, [field]: value } : step
    ));
  };

  const handleRemoveStep = (stepId: string) => {
    setSteps(steps.filter(step => step.id !== stepId).map((step, index) => ({
      ...step,
      order: index + 1
    })));
  };

  const handleMoveStep = (stepId: string, direction: 'up' | 'down') => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;
    if (direction === 'up' && stepIndex === 0) return;
    if (direction === 'down' && stepIndex === steps.length - 1) return;

    const newSteps = [...steps];
    const swapIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
    [newSteps[stepIndex], newSteps[swapIndex]] = [newSteps[swapIndex], newSteps[stepIndex]];
    
    setSteps(newSteps.map((step, index) => ({ ...step, order: index + 1 })));
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    if (!testCaseUUID && !isEditMode) {
      // For new test cases, just add locally - will be saved with test case
      const comment: TestCaseComment = {
        id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        userId: user?.id || '',
        userName: user?.displayName || user?.email || 'Unknown',
        text: newComment.trim(),
        createdAt: new Date().toISOString()
      };
      setComments([...comments, comment]);
      setNewComment('');
      return;
    }
    
    // For existing test cases, save to API immediately
    try {
      const savedComment = await apiRequest<TestCaseComment>(`/api/testcases/${testCaseUUID}/comments`, {
        method: 'POST',
        body: JSON.stringify({ text: newComment.trim() })
      }, { useAuth: true });
      
      setComments([...comments, savedComment]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to add comment' });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!testCaseUUID) {
      // For new test cases, just remove locally
      setComments(comments.filter(c => c.id !== commentId));
      return;
    }
    
    // For existing test cases, delete via API
    try {
      await apiRequest(`/api/testcases/${testCaseUUID}/comments/${commentId}`, {
        method: 'DELETE'
      }, { useAuth: true });
      
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to delete comment' });
    }
  };

  const handleSave = async () => {
    if (!testCaseTitle.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Please enter a test case title.' });
      return;
    }

    if (!selectedFeatureId) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a feature.' });
      return;
    }

    if (!selectedComponentId) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a component.' });
      return;
    }

    // Validate comment requirement for "Needs Update" status
    if (status === 'Needs Update' && comments.length === 0) {
      showTempMessage(setMessage, { type: 'error', text: 'Please add a comment explaining why this test case needs an update.' });
      return;
    }

    try {
      const validSteps = steps.filter(step => step.action.trim());

      // Base test case data (without comments for existing test cases - they're saved independently)
      const testCaseData: Record<string, unknown> = {
        title: testCaseTitle.trim(),
        componentId: selectedComponentId || undefined,
        featureId: selectedFeatureId || undefined,
        description: testCaseDescription.trim() || undefined,
        preConditions: testCasePreConditions.trim() || undefined,
        steps: validSteps.length > 0 ? validSteps : undefined,
        automated: isAutomated,
        automationCandidate: isAutomationCandidate,
        status
      };

      if (isEditMode && testCaseUUID) {
        // For existing test cases, don't include comments - they're saved via separate API
        await apiRequest<TestCase>(`/api/testcases/${testCaseUUID}`, {
          method: 'PUT',
          body: JSON.stringify(testCaseData)
        }, { useAuth: true });
        showTempMessage(setMessage, { type: 'success', text: 'Test case updated successfully' }, 3000, true);
      } else {
        // For new test cases, include comments in the initial save
        testCaseData.comments = comments;
        await apiRequest<TestCase>('/api/testcases', {
          method: 'POST',
          body: JSON.stringify(testCaseData)
        }, { useAuth: true });
        showTempMessage(setMessage, { type: 'success', text: 'Test case created successfully' }, 3000, true);
      }

      navigate('/library');
    } catch (error) {
      console.error('Error saving test case:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to save test case' });
    }
  };

  const handleCancel = () => {
    navigate('/library');
  };

  if (loading) {
    return (
      <div className="form-container">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <Toast message={message} />

      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page-title">
          {isEditMode ? (
            <>
              Edit Test Case
              {testCaseNumber && selectedProduct?.projectId && (
                <span style={{
                  marginLeft: '1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  padding: '0.25rem 0.75rem',
                  backgroundColor: 'var(--cyan-glow)',
                  color: 'var(--surface)',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>
                  {selectedProduct.projectId}-TC-{testCaseNumber}
                </span>
              )}
            </>
          ) : (
            'Create Test Case'
          )}
        </h1>
        
        {/* Status Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status:</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as TestCaseStatus)}
            style={{
              padding: '0.5rem 2rem 0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
              backgroundColor: status === 'Active' ? 'rgba(34, 197, 94, 0.1)' 
                : status === 'Draft' ? 'var(--cyan-transparent)' 
                : 'rgba(239, 68, 68, 0.1)',
              color: status === 'Active' ? '#22c55e' 
                : status === 'Draft' ? 'var(--cyan-glow)' 
                : 'var(--danger)',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2300d4ff' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.5rem center'
            }}
          >
            <option value="Active">Active</option>
            <option value="Draft">Draft</option>
            <option value="Needs Update">Needs Update</option>
          </select>
        </div>
      </div>

      {/* Main Content with Comment Sidebar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '2rem', maxWidth: '1400px' }}>
        
        {/* Left Column - Form */}
        <div>
          {/* Basic Info */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '1.5rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--grid-line)'
          }}>
            <h2 style={{
              margin: '0 0 1rem 0',
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Basic Information
            </h2>

            <div className="form-group">
              <label className="label">Title <span className="text-danger">*</span></label>
              <input
                type="text"
                value={testCaseTitle}
                onChange={(e) => setTestCaseTitle(e.target.value)}
                className="form-control"
                placeholder="Enter test case title..."
                autoFocus
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="label">Feature <span className="text-danger">*</span></label>
                <select
                  value={selectedFeatureId}
                  onChange={(e) => {
                    setSelectedFeatureId(e.target.value);
                    const currentComp = components.find(c => c.id === selectedComponentId);
                    if (currentComp && currentComp.featureId !== e.target.value) {
                      setSelectedComponentId('');
                    }
                  }}
                  className="form-control"
                >
                  <option value="">Select a feature...</option>
                  {features.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="label">Component <span className="text-danger">*</span></label>
                <select
                  value={selectedComponentId}
                  onChange={(e) => {
                    setSelectedComponentId(e.target.value);
                    if (e.target.value) {
                      const comp = components.find(c => c.id === e.target.value);
                      if (comp && comp.featureId !== selectedFeatureId) {
                        setSelectedFeatureId(comp.featureId);
                      }
                    }
                  }}
                  className="form-control"
                  disabled={!selectedFeatureId}
                >
                  <option value="">
                    {!selectedFeatureId ? 'Select a feature first...' : 'Select a component...'}
                  </option>
                  {filteredComponents.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Description & Pre-Conditions */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '1.5rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--grid-line)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Description</label>
                <textarea
                  value={testCaseDescription}
                  onChange={(e) => setTestCaseDescription(e.target.value)}
                  className="form-control"
                  rows={4}
                  placeholder="Describe the purpose of this test case..."
                  style={{ resize: 'vertical', minHeight: '100px' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Pre-Conditions</label>
                <textarea
                  value={testCasePreConditions}
                  onChange={(e) => setTestCasePreConditions(e.target.value)}
                  className="form-control"
                  rows={4}
                  placeholder="Prerequisites before running this test..."
                  style={{ resize: 'vertical', minHeight: '100px' }}
                />
              </div>
            </div>
          </div>

          {/* Automation Status */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '1.5rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--grid-line)'
          }}>
            <h2 style={{
              margin: '0 0 1rem 0',
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Automation Status
            </h2>
            <div style={{ display: 'flex', gap: '2rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', flex: 1 }}>
                <div 
                  onClick={() => {
                    setIsAutomated(!isAutomated);
                    if (!isAutomated) setIsAutomationCandidate(false);
                  }}
                  style={{
                    width: '48px',
                    height: '26px',
                    borderRadius: '13px',
                    backgroundColor: isAutomated ? 'var(--success)' : 'var(--border-color)',
                    position: 'relative',
                    transition: 'background-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: isAutomated ? '24px' : '2px',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Automated</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Has automation coverage</div>
                </div>
              </label>

              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem', 
                cursor: isAutomated ? 'not-allowed' : 'pointer', 
                flex: 1,
                opacity: isAutomated ? 0.5 : 1 
              }}>
                <div 
                  onClick={() => !isAutomated && setIsAutomationCandidate(!isAutomationCandidate)}
                  style={{
                    width: '48px',
                    height: '26px',
                    borderRadius: '13px',
                    backgroundColor: isAutomationCandidate ? 'var(--warning)' : 'var(--border-color)',
                    position: 'relative',
                    transition: 'background-color 0.2s ease',
                    cursor: isAutomated ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: isAutomationCandidate ? '24px' : '2px',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Ready to Automate</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Candidate for automation</div>
                </div>
              </label>
            </div>
          </div>

          {/* Test Steps */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '1.5rem',
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--grid-line)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{
                margin: 0,
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                Test Steps
                {steps.length > 0 && (
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    background: 'var(--cyan-glow)',
                    color: 'var(--surface)',
                    borderRadius: '10px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {steps.length}
                  </span>
                )}
              </h2>
              {!showStepsSection && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowStepsSection(true);
                    if (steps.length === 0) handleAddStep();
                  }}
                >
                  + Add Steps
                </button>
              )}
            </div>

            {showStepsSection && (
              <>
                {steps.map((step, index) => (
                  <div key={step.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr 1fr 1fr 80px',
                    gap: '0.75rem',
                    alignItems: 'start',
                    marginBottom: '0.75rem',
                    padding: '0.75rem',
                    background: 'var(--surface)',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, var(--cyan-glow) 0%, var(--primary-color) 100%)',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {index + 1}
                    </div>
                    <input
                      type="text"
                      value={step.action}
                      onChange={(e) => handleUpdateStep(step.id, 'action', e.target.value)}
                      className="form-control"
                      placeholder="Action..."
                    />
                    <input
                      type="text"
                      value={step.data || ''}
                      onChange={(e) => handleUpdateStep(step.id, 'data', e.target.value)}
                      className="form-control"
                      placeholder="Test data..."
                    />
                    <input
                      type="text"
                      value={step.expectedResult || ''}
                      onChange={(e) => handleUpdateStep(step.id, 'expectedResult', e.target.value)}
                      className="form-control"
                      placeholder="Expected result..."
                    />
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        type="button"
                        onClick={() => handleMoveStep(step.id, 'up')}
                        disabled={index === 0}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'none',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          opacity: index === 0 ? 0.3 : 1,
                          color: 'var(--text-secondary)'
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveStep(step.id, 'down')}
                        disabled={index === steps.length - 1}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'none',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: index === steps.length - 1 ? 'not-allowed' : 'pointer',
                          opacity: index === steps.length - 1 ? 0.3 : 1,
                          color: 'var(--text-secondary)'
                        }}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveStep(step.id)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'none',
                          border: '1px solid var(--danger)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: 'var(--danger)'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleAddStep}
                  style={{ marginTop: '0.5rem' }}
                >
                  + Add Step
                </button>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingBottom: '2rem' }}>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              {isEditMode ? 'Save Changes' : 'Create Test Case'}
            </button>
          </div>
        </div>

        {/* Right Column - Comments Sidebar */}
        <div style={{
          position: 'sticky',
          top: '1rem',
          height: 'fit-content',
          maxHeight: 'calc(100vh - 8rem)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            backgroundColor: 'var(--surface-elevated)',
            borderRadius: '8px',
            border: '1px solid var(--grid-line)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: 'calc(100vh - 8rem)'
          }}>
            {/* Comments Header */}
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--grid-line)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '1.25rem' }}>💬</span>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Comments
              </h3>
              {comments.length > 0 && (
                <span style={{
                  padding: '0.125rem 0.5rem',
                  background: 'var(--cyan-glow)',
                  color: 'var(--surface)',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {comments.length}
                </span>
              )}
            </div>

            {/* Comments List */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '1rem'
            }}>
              {comments.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem 1rem', 
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.5 }}>💬</div>
                  No comments yet.
                  {status === 'Needs Update' && (
                    <div style={{ color: 'var(--danger)', marginTop: '0.5rem', fontWeight: 500 }}>
                      A comment is required for "Needs Update" status.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {comments.map(comment => (
                    <div key={comment.id} style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface)',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      position: 'relative'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '0.25rem'
                      }}>
                        <span style={{ 
                          fontWeight: 600, 
                          fontSize: '0.8rem', 
                          color: 'var(--cyan-glow)' 
                        }}>
                          {comment.userName}
                        </span>
                        {/* Delete button - only show for user's own comments or always allow in edit mode */}
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            padding: '0.125rem 0.25rem',
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--danger)';
                            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--text-muted)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Delete comment"
                        >
                          ✕
                        </button>
                      </div>
                      <div style={{ 
                        fontSize: '0.7rem', 
                        color: 'var(--text-muted)',
                        marginBottom: '0.5rem'
                      }}>
                        {new Date(comment.createdAt).toLocaleDateString()} at {new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '0.875rem', 
                        color: 'var(--text-primary)',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {comment.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Comment */}
            <div style={{
              padding: '1rem',
              borderTop: '1px solid var(--grid-line)'
            }}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="form-control"
                rows={3}
                style={{ 
                  resize: 'none',
                  marginBottom: '0.75rem',
                  fontSize: '0.875rem'
                }}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                style={{ width: '100%' }}
              >
                Add Comment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTestCasePage;
